// What the Import files button does once pressed: save every file that's on through the
// existing mutations (importSave.ts) and sum up what happened.
// Kept out of the component so the dialog only sequences; the decisions are in utils/import.

import graphClient from '../../utils/graphql/graph_client';
import { CreateRecording } from '../../utils/graphql/recorded-trail-graph';
import { CreateHikeWithPlannedTrails } from '../../utils/graphql/hikes-graph';
import { CreateWaypointGroup, CreateWaypoints } from '../../utils/graphql/waypoints-graph';
import { IMPORT_BAGGED_PEAKS_CSV_MUTATION } from '../peak_bagging/queries';
import { importTelemetry, MAX_CSV_BYTES, peakCsvErrorResult, type ImportResult as PeakCsvCheck } from '../peak_bagging/peakBagCsv';
import { trackImportEvent } from '../../utils/import/importTelemetry';
import { getElevationData } from '../../utils/helpers/HiikerElevationApiHelper';
import { mapMatchCoordinates } from '../../utils/models/mapMatch';
import { willImport, type ImportCounts, type ImportFileEntry, type ImportScreenState } from '../../utils/import/importScreenState';
import { saveImports, type ImportCreated, type ImportSaveDeps, type ImportSavedResult } from '../../utils/import/importSave';
import type { Translate } from '../../utils/import/importTexts';

export type { ImportSavedResult };

export type ImportOutcome = { kind: 'saved' } & ImportSavedResult;

/** The counts of what was actually created, for the result. */
export function createdCounts(created: ImportCreated[]): ImportCounts {
  return {
    recordings: created.filter(c => c.destination === 'recordings').length,
    plannedHikes: created.filter(c => c.destination === 'plannedHikes').length,
    waypointGroups: created.filter(c => c.destination === 'waypoints').length,
    peaks: created.filter(c => c.destination === 'peaks').reduce((n, c) => n + (c.count ?? 1), 0)
  };
}

/**
 * A bagged-peaks CSV's dry run: which rows the server matches to a peak. Never throws — a file
 * over the server's limit, or a request that fails, comes back as that error.
 */
export async function checkPeakCsv(
  file: Blob,
  request: (document: unknown, variables: Record<string, unknown>) => Promise<any> = (d, v) => graphClient.request(d as any, v)
): Promise<PeakCsvCheck> {
  if (file.size > MAX_CSV_BYTES) return peakCsvErrorResult('too_large');
  try {
    const res = await request(IMPORT_BAGGED_PEAKS_CSV_MUTATION, { input: { csv: await file.text(), commit: false } });
    return res?.importBaggedPeaksCsv ?? peakCsvErrorResult('generic');
  } catch {
    return peakCsvErrorResult('generic');
  }
}

export function saveDeps(t: Translate, fileOf: (entry: ImportFileEntry) => Blob | null = () => null): ImportSaveDeps {
  return {
    request: (document, variables) => graphClient.request(document as any, variables),
    elevation: coords => getElevationData(coords),
    mapMatch: (coords, profile) => mapMatchCoordinates(coords, profile),
    mutations: {
      createRecording: CreateRecording,
      createHike: CreateHikeWithPlannedTrails,
      createWaypoints: CreateWaypoints,
      createWaypointGroup: CreateWaypointGroup,
      importBaggedPeaksCsv: IMPORT_BAGGED_PEAKS_CSV_MUTATION
    },
    peakCsvText: async entry => {
      const file = fileOf(entry);
      if (!file) throw new Error('the CSV is no longer available');
      return file.text();
    },
    onPeaksImported: result => trackImportEvent('peak_csv_import', importTelemetry(result)),
    texts: {
      defaultTitle: t('import_files.default_title'),
      unnamedWaypoint: t('waypoint_dialog.waypoint_options_dialog_module.imported_waypoint'),
      unnamedHighlight: (n: number) => t('import_files.imported_highlight', { n })
    }
  };
}

/** The button: saves every importing file, one at a time, and sums up what happened. */
export async function runImport(s: ImportScreenState, t: Translate, deps: ImportSaveDeps = saveDeps(t)): Promise<ImportOutcome> {
  const { created, failures, savedFiles, snap } = await saveImports(s.entries.filter(willImport), deps, s.snaps ?? {});
  return { kind: 'saved', created, failures, snap, counts: createdCounts(created), notImported: s.entries.length - savedFiles };
}
